import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import moment from "moment";

import WPHeading from "../../../../../../packages/WPHeading";

import {
  LICENSE_ASSIGNED_DATE_KEY,
  LICENSE_CARD_DETAILS_CONFIG,
  LICENSE_DATE_DISPLAY_FORMAT,
  LICENSE_EXPIRY_DATE_KEY,
  LICENSE_SECTIONS_CONFIG,
} from "../UserLicenseDetails.constants";
import UserLicenseListSection from "./UserLicenseListSection";

import { LICENSE_STATUS } from "../../../../../../constants/LicenseManagement.constants";

import { combineClassNames } from "../../../../../../utils/appUtils";
import { groupLicensePermissionsByCategory } from "../../../../../../utils/LicenseUtils";

import "./UserLicenseCard.scss";

const UserLicenseCard = ({ license }) => {
  const { t } = useTranslation();
  const { licensePermissions } = license || {};

  const { licenseCardDetails, isLicenseExpiredOrTerminated } = useMemo(() => {
    const today = moment().startOf("day");
    const details = LICENSE_CARD_DETAILS_CONFIG.map(
      ({ icon: Icon, label, valueKey, showKey, iconProps }) => {
        const rawValue = license?.[valueKey];

        const isDateField =
          valueKey === LICENSE_ASSIGNED_DATE_KEY ||
          valueKey === LICENSE_EXPIRY_DATE_KEY;
        const parsedDate = isDateField && rawValue ? moment(rawValue) : null;
        const isValidDate = !!parsedDate && parsedDate.isValid();
        const isPastOrToday =
          isValidDate && parsedDate.isSameOrBefore(today, "day");

        const isLicenseTerminated =
          license?.licenseStatus === LICENSE_STATUS.terminated;
        const isLicenseExpired =
          license?.licenseStatus === LICENSE_STATUS.expired;

        const isExpired =
          valueKey === LICENSE_EXPIRY_DATE_KEY &&
          isPastOrToday &&
          isLicenseExpired;
        const isTerminated =
          valueKey === LICENSE_EXPIRY_DATE_KEY &&
          isPastOrToday &&
          isLicenseTerminated;

        const value =
          valueKey === LICENSE_EXPIRY_DATE_KEY
            ? isValidDate
              ? parsedDate.format(LICENSE_DATE_DISPLAY_FORMAT)
              : "N/A"
            : isDateField && isValidDate
              ? parsedDate.format(LICENSE_DATE_DISPLAY_FORMAT)
              : rawValue;
        const displayLabel =
          isTerminated && valueKey === LICENSE_EXPIRY_DATE_KEY
            ? t("licenseProfile.licenseTerminatedOn")
            : isExpired && valueKey === LICENSE_EXPIRY_DATE_KEY
              ? t("licenseProfile.licenseExpiredOn")
              : t(label);

        return {
          icon: <Icon {...iconProps} />,
          label: `${displayLabel}:`,
          value,
          isExpiredOrTerminated: isExpired || isTerminated,
          show: showKey
            ? !!license?.[showKey] || valueKey === LICENSE_EXPIRY_DATE_KEY
            : true,
        };
      },
    );

    const expiredOrTerminated =
      details?.some((detail) => detail.isExpiredOrTerminated) ?? false;
    return {
      licenseCardDetails: details,
      isLicenseExpiredOrTerminated: expiredOrTerminated,
    };
  }, [license, t]);

  const groupedPermissions = useMemo(
    () => groupLicensePermissionsByCategory(licensePermissions),
    [licensePermissions],
  );

  const licenseSections = useMemo(
    () =>
      LICENSE_SECTIONS_CONFIG.map(({ id, label, tag }) => ({
        id,
        label: t(label),
        licenseSectionData: groupedPermissions[tag] ?? [],
      })),
    [groupedPermissions, t],
  );

  return (
    <div className="user-license-card-content">
      <div className="user-license-card-details">
        {licenseCardDetails?.map(
          ({ icon, label, value, show, isExpiredOrTerminated }, idx) =>
            show ? (
              <div
                className={combineClassNames(
                  "user-license-detail-card",
                  "user-license-card-name",
                  `user-license-card-${LICENSE_CARD_DETAILS_CONFIG[idx]?.valueKey}`,
                  isExpiredOrTerminated ? "expired-terminated-license" : "",
                )}
                key={label}
                data-test-id={`user-license-${LICENSE_CARD_DETAILS_CONFIG[idx]?.valueKey}`}
              >
                <span className="user-license-card-icon">{icon}</span>
                <WPHeading
                  text={label}
                  color="var(--du-color-grey)"
                  size="small"
                  secondary
                  fontNoto
                  id={`user-license-label-${LICENSE_CARD_DETAILS_CONFIG[idx]?.valueKey}`}
                />
                <div className="user-license-card-value">
                  <WPHeading
                    text={value}
                    size="small"
                    secondary
                    wrapContent
                    fontNoto
                    type="default"
                    tooltip={value}
                    id={`user-license-value-${LICENSE_CARD_DETAILS_CONFIG[idx]?.valueKey}`}
                  />
                </div>
              </div>
            ) : null,
        )}
      </div>
      <div className="user-license-permissions">
        <WPHeading
          text={t("licenseProfile.licensePermissions")}
          size="small"
          secondary
          id="user-license-permissions-title"
        />
        <WPHeading
          size="xSmall"
          type="default"
          text={t("licenseProfile.licensePermissionsText")}
          wrapContent={false}
          secondary
          color="var(--du-color-grey)"
          id="user-license-permissions-desc"
        />
      </div>
      <div className="user-license-list-sections">
        {licenseSections?.map((section, idx) => (
          <React.Fragment key={`${section.label}-${idx}`}>
            <div className="user-license-list-section-flex">
              <UserLicenseListSection
                title={section.label}
                licenseSectionData={section.licenseSectionData}
                id={`license-permissions-${section.id}`}
                isLicenseExpiredOrTerminated={isLicenseExpiredOrTerminated}
              />
            </div>
            {idx < licenseSections.length - 1 && (
              <div className="user-license-list-section-divider" />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default UserLicenseCard;
